import { ChangeEvent } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { Search, ListFilter } from "lucide-react";
import { Input } from "../../ui/input";
import { SearchInputProps } from "./types";
import { cn } from "../../utils";
import { INPUT_PROTECTION_ATTRIBUTES } from "../../consts/html";
import { SearchSize, SearchVariant } from "./consts";

export const SearchInput = ({
  containerClassName,
  className: propsClassName,
  searchSize = SearchSize.SMALL,
  disabled = false,
  disableFilterIcon = true,
  debounceMs = 300,
  onDebouncedChange,
  onChange,
  ...rest
}: SearchInputProps) => {
  const debouncedOnChange = useDebounceCallback(
    (value: string) => onDebouncedChange?.(value),
    debounceMs,
  );

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    debouncedOnChange(e.target.value);
  };

  const inputSizeClass =
    searchSize === SearchSize.LARGE
      ? "pl-12 py-3 pr-4 h-[45px]"
      : "pl-9 py-1.5 pr-2 h-[33px]";

  const iconClass = cn(
    "absolute top-1/2 size-4 -translate-y-1/2",
    disabled
      ? "text-icon-input-neutral-disabled"
      : "text-icon-input-neutral-idle",
  );

  const inputClass = cn(
    "text-text-general-neutral-strong dark:border-border-input-neutral-idle dark:bg-fill-input-neutral-idle dark:placeholder:text-text-general-neutral-muted pl-9",
    {
      "bg-transparent": rest.variant === SearchVariant.GHOST,
    },
    { "pr-9": !disableFilterIcon },
  );

  return (
    <div
      className={cn("qodo qodo-search-input", "relative", containerClassName)}
    >
      <Search className={cn(iconClass, "left-3")} />
      <Input
        className={cn(inputSizeClass, inputClass, propsClassName)}
        disabled={disabled}
        onChange={onInputChange}
        {...rest}
        {...INPUT_PROTECTION_ATTRIBUTES}
      />
      {!disableFilterIcon && (
        <ListFilter className={cn(iconClass, "right-3")} />
      )}
    </div>
  );
};
